import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import {
  SUGGESTION_ANNOTATION_COMMAND_TYPES,
  SUGGESTION_ANNOTATION_PREDICATE_TYPES,
} from 'modules/common/viewer/pdfViewer/annotationCommands/consts';
import type {
  SuggestionAnnotationRaw,
  SuggestionAnnotationStateUpdate,
} from 'modules/common/viewer/pdfViewer/annotationCommands/types';
import { suggestionAnnotations } from 'modules/common/viewer/pdfViewer/data/actions';

export type AnnotationVisualProps = {
  colorCode: string;
  opacity: number;
  borderWidth: number;
  isBleedingOrange?: boolean;
};

/**
 * Shared orchestration hook for the LOAD → HIDE-stale → MERGE_STATE_MULTIPLE
 * dispatch cycle.
 *
 * The caller is responsible for computing what to show and how — this hook
 * just applies those decisions to the annotation command queue.
 */
export function useAnnotationOrchestration(params: {
  /** All annotations to display in the viewer */
  allAnnotations: SuggestionAnnotationRaw[];
  /** Per-annotation visibility — return true to hide */
  isHidden: (annot: SuggestionAnnotationRaw) => boolean;
  /** Per-annotation visual properties (color, opacity, border) */
  getVisualProps: (annot: SuggestionAnnotationRaw) => AnnotationVisualProps;
  /** Per-annotation whiteout state (optional — return true if whiteout) */
  isWhiteout?: (annot: SuggestionAnnotationRaw) => boolean;
}) {
  const { allAnnotations, isHidden, getVisualProps, isWhiteout } = params;
  const dispatch = useDispatch();

  // load annotations
  useEffect(() => {
    dispatch(
      suggestionAnnotations.queueCommands({
        commands: [
          {
            type: SUGGESTION_ANNOTATION_COMMAND_TYPES.LOAD,
            annotations: allAnnotations,
          },
        ],
      }),
    );
  }, [dispatch, allAnnotations]);

  // Apply visibility and visual state
  useEffect(() => {
    // get current state, then get the next state, then queue commands
    const nextState: SuggestionAnnotationStateUpdate = {};
    for (const annot of allAnnotations) {
      const visualProps = getVisualProps(annot);
      // NOTE: do not manipulate focus and hidden here as that happens transiently from
      //  focus commands
      nextState[annot.id] = {
        isHidden: isHidden(annot),
        isWhiteout: isWhiteout?.(annot) ?? false,
        ...visualProps,
      };
    }
    dispatch(
      suggestionAnnotations.queueCommands({
        commands: [
          // turn all off that are no longer referenced
          {
            type: SUGGESTION_ANNOTATION_COMMAND_TYPES.HIDE,
            predicate: {
              type: SUGGESTION_ANNOTATION_PREDICATE_TYPES.NOT_IN_NEXT_STATE,
              nextStateAnnotationIds: Object.keys(nextState),
            },
          },
          {
            type: SUGGESTION_ANNOTATION_COMMAND_TYPES.MERGE_STATE_MULTIPLE,
            mergeState: nextState,
          },
        ],
      }),
    );
  }, [dispatch, allAnnotations, isHidden, getVisualProps, isWhiteout]);
}
